import React, { useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PageLayout, PageHeader } from '../../components'
import { StatusChip, LoadingSpinner } from '../../components/atoms'
import useBillingPlanData from '../../hooks/useBillingPlanData'
import { authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import styles from './AdminBillingPlanManagement.module.scss'

const formatCurrency = (amountMinor, currency = 'CAD') => {
  const amount = (amountMinor / 100).toFixed(2)
  return `$${amount} ${currency}`
}

const formatDate = (dateStr) => {
  if (!dateStr) return null
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const EMPTY_FORM = {
  plan_level_name: '',
  currency: 'CAD',
  fixed_monthly_fee: '',
  in_plan_per_minute: '0.00',
  overage_per_minute: '',
  effective_date: ''
}

const FORM_COPY = {
  create: { title: 'Create Billing Plan', submit: 'Create Plan', submitting: 'Creating...', errorPrefix: 'Failed to create plan' },
  schedule: { title: 'Schedule Plan Change', submit: 'Schedule Change', submitting: 'Scheduling...', errorPrefix: 'Failed to schedule plan change' },
  scheduleEdit: { title: 'Edit Scheduled Change', submit: 'Schedule Change', submitting: 'Scheduling...', errorPrefix: 'Failed to update scheduled change' },
  edit: { title: 'Edit Current Plan', submit: 'Save Changes', submitting: 'Saving...', errorPrefix: 'Failed to update plan' }
}

const SchedulePlanChangeForm = ({ campaignId, billingCustomerId, onSuccess, onCancel, initialData, existingChangeId, planId, mode = 'schedule' }) => {
  const [form, setForm] = useState(initialData || EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState(null)

  const resolvedMode = mode === 'schedule' && initialData ? 'scheduleEdit' : mode
  const copy = FORM_COPY[resolvedMode] || FORM_COPY.schedule
  const isEdit = mode === 'edit'

  const handleChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
    setFormError(null)
  }, [])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    setFormError(null)

    const missingEffective = !isEdit && !form.effective_date
    if (missingEffective || !form.fixed_monthly_fee || !form.overage_per_minute) {
      setFormError('Please fill in all required fields.')
      return
    }

    setSubmitting(true)
    try {
      const planData = {
        billing_customer_id: billingCustomerId,
        plan_level_name: form.plan_level_name || 'Custom',
        currency: form.currency,
        fixed_monthly_fee_minor: Math.round(parseFloat(form.fixed_monthly_fee) * 100),
        in_plan_per_minute_minor: Math.round(parseFloat(form.in_plan_per_minute || '0') * 100),
        overage_per_minute_minor: Math.round(parseFloat(form.overage_per_minute) * 100),
        due_days: 30
      }

      if (mode === 'create') {
        await authenticatedFetchJson(apiEndpoints.billingPlans, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaign_id: parseInt(campaignId, 10),
            effective_from: form.effective_date,
            ...planData
          })
        })
      } else if (mode === 'edit') {
        const { billing_customer_id: _unused, ...updatable } = planData
        await authenticatedFetchJson(`${apiEndpoints.billingPlans}/${planId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatable)
        })
      } else {
        await authenticatedFetchJson(`${apiEndpoints.billingPlans.replace('/plans', '/plan-changes')}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            campaign_id: parseInt(campaignId, 10),
            effective_date: form.effective_date,
            new_plan: planData
          })
        })
      }

      // Delete old scheduled change only after the new one succeeds
      if (existingChangeId) {
        try {
          await authenticatedFetchJson(
            `${apiEndpoints.billingPlans.replace('/plans', '/plan-changes')}/${existingChangeId}`,
            { method: 'DELETE' }
          )
        } catch (deleteErr) {
          logger.error('Failed to cancel previous scheduled change:', deleteErr)
          setFormError('Plan saved, but failed to cancel the previous scheduled change. Please cancel it manually.')
          setSubmitting(false)
          onSuccess()
          return
        }
      }

      onSuccess()
    } catch (err) {
      setFormError(err.message || copy.errorPrefix)
      logger.error(`${copy.errorPrefix}:`, err)
    } finally {
      setSubmitting(false)
    }
  }, [form, campaignId, billingCustomerId, onSuccess, existingChangeId, mode, isEdit, planId, copy.errorPrefix])

  return (
    <form className={styles.scheduleForm} onSubmit={handleSubmit} data-testid='schedule-form'>
      <h4 className={styles.subTitle}>{copy.title}</h4>

      <div className={styles.formGrid}>
        {!isEdit && (
          <div className={styles.formField}>
            <label htmlFor='effective_date'>Effective Date *</label>
            <input id='effective_date' type='date' value={form.effective_date} onChange={(e) => handleChange('effective_date', e.target.value)} required />
          </div>
        )}
        <div className={styles.formField}>
          <label htmlFor='plan_level_name'>Plan Name</label>
          <input id='plan_level_name' type='text' value={form.plan_level_name} onChange={(e) => handleChange('plan_level_name', e.target.value)} placeholder='e.g. Pro' />
        </div>
        <div className={styles.formField}>
          <label htmlFor='currency'>Currency</label>
          <select id='currency' value={form.currency} onChange={(e) => handleChange('currency', e.target.value)}>
            <option value='CAD'>CAD</option>
            <option value='USD'>USD</option>
          </select>
        </div>
        <div className={styles.formField}>
          <label htmlFor='fixed_monthly_fee'>Monthly Fee *</label>
          <div className={styles.dollarInput}>
            <span className={styles.dollarSign}>$</span>
            <input id='fixed_monthly_fee' type='number' min='0' step='0.01' value={form.fixed_monthly_fee} onChange={(e) => handleChange('fixed_monthly_fee', e.target.value)} placeholder='150.00' required />
          </div>
        </div>
        <div className={styles.formField}>
          <label htmlFor='overage_per_minute'>Overage Rate (per min) *</label>
          <div className={styles.dollarInput}>
            <span className={styles.dollarSign}>$</span>
            <input id='overage_per_minute' type='number' min='0' step='0.01' value={form.overage_per_minute} onChange={(e) => handleChange('overage_per_minute', e.target.value)} placeholder='0.75' required />
          </div>
        </div>
      </div>

      {formError && <p className={styles.formError}>{formError}</p>}

      <div className={styles.formActions}>
        <button type='submit' className={styles.submitButton} disabled={submitting}>
          {submitting ? copy.submitting : copy.submit}
        </button>
        <button type='button' className={styles.cancelFormButton} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

SchedulePlanChangeForm.propTypes = {
  campaignId: PropTypes.string,
  billingCustomerId: PropTypes.number,
  onSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  existingChangeId: PropTypes.number,
  planId: PropTypes.number,
  mode: PropTypes.oneOf(['create', 'schedule', 'edit'])
}

export const PlanManagementContent = ({ campaignId, showBackButton = false, onBack }) => {
  const { plans, scheduledChanges, billingCustomerId, loading, error, refetch } = useBillingPlanData(campaignId)
  const [selectedPlanId, setSelectedPlanId] = useState(null)
  const [cancellingId, setCancellingId] = useState(null)
  const [showScheduleForm, setShowScheduleForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingChange, setEditingChange] = useState(null)

  const selectedPlan = useMemo(() => {
    if (!selectedPlanId) return plans.find(p => p.is_active) || plans[0] || null
    return plans.find(p => p.id === selectedPlanId) || null
  }, [plans, selectedPlanId])

  const handleCancelChange = useCallback(async (changeId) => {
    try {
      setCancellingId(changeId)
      await authenticatedFetchJson(
        `${apiEndpoints.billingPlans.replace('/plans', '/plan-changes')}/${changeId}`,
        { method: 'DELETE' }
      )
      await refetch()
    } catch (err) {
      logger.error('Failed to cancel plan change:', err)
    } finally {
      setCancellingId(null)
    }
  }, [refetch])

  const handleEditChange = useCallback((change) => {
    setEditingChange(change)
    setShowScheduleForm(true)
  }, [])

  const handleScheduleSuccess = useCallback(() => {
    setShowScheduleForm(false)
    setEditingChange(null)
    refetch()
  }, [refetch])

  const handleCancelForm = useCallback(() => {
    setShowScheduleForm(false)
    setEditingChange(null)
  }, [])

  const handleEditPlanSuccess = useCallback(() => {
    setShowEditForm(false)
    refetch()
  }, [refetch])

  // Build form initial data from the current active plan (for in-place edit)
  const editCurrentPlanFormData = useMemo(() => {
    if (!selectedPlan) return null
    return {
      plan_level_name: selectedPlan.plan_level_name || '',
      currency: selectedPlan.currency || 'CAD',
      fixed_monthly_fee: String((selectedPlan.fixed_monthly_fee_minor || 0) / 100),
      in_plan_per_minute: String((selectedPlan.in_plan_per_minute_minor || 0) / 100),
      overage_per_minute: String((selectedPlan.overage_per_minute_minor || 0) / 100),
      effective_date: '' // unused in edit mode
    }
  }, [selectedPlan])

  // Build form initial data from an existing scheduled change (for editing)
  const editFormData = useMemo(() => {
    if (!editingChange) return null
    const snap = editingChange.new_plan_snapshot || {}
    return {
      plan_level_name: snap.plan_level_name || '',
      currency: snap.currency || 'CAD',
      fixed_monthly_fee: String((snap.fixed_monthly_fee_minor || 0) / 100),
      in_plan_per_minute: String((snap.in_plan_per_minute_minor || 0) / 100),
      overage_per_minute: String((snap.overage_per_minute_minor || 0) / 100),
      due_days: String(snap.due_days || '30'),
      effective_date: editingChange.effective_date ? editingChange.effective_date.split('T')[0] : ''
    }
  }, [editingChange])

  if (!campaignId) {
    return (
      <div className={styles.emptyState}>
        <p>No campaign selected.</p>
      </div>
    )
  }

  if (loading) {
    return <LoadingSpinner message='Loading plans...' />
  }

  if (error) {
    return <div className={styles.errorState}><p>{error}</p></div>
  }

  return (
    <>
      {showBackButton && onBack && (
        <button className={styles.backButton} onClick={onBack}>
          <FontAwesomeIcon icon='chevron-left' /> Back
        </button>
      )}

      <div className={styles.layout}>
        {/* Plan History + Upcoming */}
        <div className={styles.historyPanel}>
          {/* Upcoming Scheduled Changes */}
          {scheduledChanges.length > 0 && (
            <>
              <h3 className={styles.sectionTitle}>Upcoming</h3>
              <div className={styles.planList}>
                {scheduledChanges.map(change => (
                  <div key={change.id} className={styles.upcomingItem} data-testid={`upcoming-${change.id}`}>
                    <div className={styles.planItemHeader}>
                      <span className={styles.planName}>{change.new_plan_snapshot?.plan_level_name || 'Scheduled'}</span>
                      <StatusChip label='Scheduled' color='warning' size='small' />
                    </div>
                    <div className={styles.planItemDates}>
                      Effective: {formatDate(change.effective_date)}
                    </div>
                    <div className={styles.planItemFee}>
                      {formatCurrency(change.new_plan_snapshot?.fixed_monthly_fee_minor || 0, change.new_plan_snapshot?.currency)}/mo
                    </div>
                    <div className={styles.upcomingActions}>
                      <button
                        className={styles.editButton}
                        onClick={() => handleEditChange(change)}
                        data-testid={`edit-change-${change.id}`}
                      >
                        Edit
                      </button>
                      <button
                        className={styles.cancelButton}
                        onClick={() => handleCancelChange(change.id)}
                        disabled={cancellingId === change.id}
                        data-testid={`cancel-change-${change.id}`}
                      >
                        {cancellingId === change.id ? 'Cancelling...' : 'Cancel'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <h3 className={styles.sectionTitle}>Plan History</h3>
          {plans.length === 0 ? (
            <div className={styles.emptyPlanState}>
              <p className={styles.emptyText}>No plans configured yet.</p>
              {!showCreateForm && billingCustomerId && (
                <button
                  className={styles.createPlanButton}
                  onClick={() => setShowCreateForm(true)}
                  data-testid='create-first-plan-btn'
                >
                  Create First Plan
                </button>
              )}
            </div>
          ) : (
            <div className={styles.planList}>
              {plans.map(plan => (
                <button
                  key={plan.id}
                  className={`${styles.planItem} ${selectedPlanId === plan.id || (!selectedPlanId && plan.is_active) ? styles.planItemActive : ''}`}
                  onClick={() => setSelectedPlanId(plan.id)}
                  data-testid={`plan-item-${plan.id}`}
                >
                  <div className={styles.planItemHeader}>
                    <span className={styles.planName}>{plan.plan_level_name || 'Plan'}</span>
                    {plan.is_active && <StatusChip label='Active' color='success' size='small' />}
                  </div>
                  <div className={styles.planItemDates}>
                    {formatDate(plan.effective_from)} {plan.effective_to ? `→ ${formatDate(plan.effective_to)}` : '→ Present'}
                  </div>
                  <div className={styles.planItemFee}>
                    {formatCurrency(plan.fixed_monthly_fee_minor, plan.currency)}/mo
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Plan Details */}
        <div className={styles.detailPanel}>
          <div className={styles.detailHeader}>
            <h3 className={styles.sectionTitle}>
              {selectedPlan ? (selectedPlan.plan_level_name || 'Plan Details') : 'No Plan Selected'}
            </h3>
            {selectedPlan?.is_active && !showScheduleForm && !showEditForm && (
              <div className={styles.detailHeaderActions}>
                <button
                  className={styles.editPlanButton}
                  onClick={() => { setShowEditForm(true) }}
                  data-testid='edit-plan-btn'
                >
                  Edit Current Plan
                </button>
                <button
                  className={styles.scheduleButton}
                  onClick={() => { setEditingChange(null); setShowScheduleForm(true) }}
                  data-testid='schedule-change-btn'
                >
                  Schedule Change
                </button>
              </div>
            )}
          </div>

          {selectedPlan ? (
            <div className={styles.planDetails}>
              <div className={styles.detailGrid}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Currency</span>
                  <span className={styles.detailValue}>{selectedPlan.currency}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Monthly Fee</span>
                  <span className={styles.detailValue}>{formatCurrency(selectedPlan.fixed_monthly_fee_minor, selectedPlan.currency)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Included Minutes</span>
                  <span className={styles.detailValue}>
                    {selectedPlan.campaign_plan_minutes || 0}
                    <span className={styles.detailHint}> (from campaign allocation)</span>
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>In-Plan Rate</span>
                  <span className={styles.detailValue}>{formatCurrency(selectedPlan.in_plan_per_minute_minor, selectedPlan.currency)}/min</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Overage Rate</span>
                  <span className={styles.detailValue}>{formatCurrency(selectedPlan.overage_per_minute_minor, selectedPlan.currency)}/min</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Tax</span>
                  <span className={styles.detailValue}>
                    Computed by Stripe at invoice time
                    <span className={styles.detailHint}> (based on customer address)</span>
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Effective From</span>
                  <span className={styles.detailValue}>{formatDate(selectedPlan.effective_from)}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Effective To</span>
                  <span className={styles.detailValue}>{selectedPlan.effective_to ? formatDate(selectedPlan.effective_to) : 'Present'}</span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Status</span>
                  <StatusChip
                    label={selectedPlan.is_active ? 'Active' : 'Inactive'}
                    color={selectedPlan.is_active ? 'success' : 'neutral'}
                    size='small'
                  />
                </div>
              </div>

              {/* Schedule / Edit Form */}
              {showScheduleForm && (
                <SchedulePlanChangeForm
                  campaignId={campaignId}
                  billingCustomerId={selectedPlan.billing_customer_id}
                  onSuccess={handleScheduleSuccess}
                  onCancel={handleCancelForm}
                  initialData={editFormData}
                  existingChangeId={editingChange?.id}
                />
              )}

              {/* Edit Current Plan Form (in-place update) */}
              {showEditForm && (
                <SchedulePlanChangeForm
                  campaignId={campaignId}
                  billingCustomerId={selectedPlan.billing_customer_id}
                  planId={selectedPlan.id}
                  onSuccess={handleEditPlanSuccess}
                  onCancel={() => setShowEditForm(false)}
                  initialData={editCurrentPlanFormData}
                  mode='edit'
                />
              )}
            </div>
          ) : (
            showCreateForm && billingCustomerId ? (
              <SchedulePlanChangeForm
                campaignId={campaignId}
                billingCustomerId={billingCustomerId}
                onSuccess={() => { setShowCreateForm(false); refetch() }}
                onCancel={() => setShowCreateForm(false)}
                mode='create'
              />
            ) : (
              <p className={styles.emptyText}>Select a plan from the history to view details.</p>
            )
          )}
        </div>
      </div>
    </>
  )
}

PlanManagementContent.propTypes = {
  campaignId: PropTypes.string,
  showBackButton: PropTypes.bool,
  onBack: PropTypes.func
}

const AdminBillingPlanManagement = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const campaignId = searchParams.get('campaign_id')

  if (!campaignId) {
    return (
      <PageLayout>
        <PageHeader title='Plan Management' subtitle='Select a campaign to manage its billing plan' />
        <div className={styles.emptyState}>
          <p>Navigate here from a campaign or billing profile to manage plans.</p>
          <p className={styles.hint}>Use <code>?campaign_id=X</code> in the URL.</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <PageHeader title='Plan Management' subtitle={`Campaign #${campaignId}`} />
      <PlanManagementContent
        campaignId={campaignId}
        showBackButton
        onBack={() => navigate('/billing/admin')}
      />
    </PageLayout>
  )
}

export default AdminBillingPlanManagement
